import { inject, Injectable } from '@angular/core';
import { ErrorHandlerService, FlashMessageService, FlashMessageType, SpinnerOverlayService } from '@libs/shared/common';
import {
  CartItemDeletePayloadModel,
  CartItemModel,
  CartItemPayloadModel,
  CartModel,
  ConfigurableBundleAddToCartModel,
  ShopCollectionResponseModel,
  ShopErrorItemModel,
  VoucherListItem,
} from '@libs/shop/models';
import { catchError, defer, finalize, map, Observable, switchMap, tap, throwError } from 'rxjs';
import { ShopErrorParserService } from '../shop-error-parser';
import { AddConfigurableBundleToCartActionData, CartQueueActionData, CartQueueActionType, CartQueueItem } from '../state';
import { CartRepositoryService } from './cart-repository.service';

@Injectable({ providedIn: 'root' })
export class CartService {
  private readonly repository: CartRepositoryService = inject(CartRepositoryService);
  private readonly spinnerService: SpinnerOverlayService = inject(SpinnerOverlayService);
  private readonly flashMessageService: FlashMessageService = inject(FlashMessageService);
  private readonly shopErrorParser: ShopErrorParserService = inject(ShopErrorParserService);
  private readonly errorHandler: ErrorHandlerService = inject(ErrorHandlerService);

  public getCart(): Observable<CartModel | undefined> {
    return this.repository.getCart();
  }

  public setCart(cart: CartModel): void {
    this.repository.setCart(cart);
  }

  public setCartId(id: string): void {
    this.repository.setCartId(id);
  }

  public getCartItemsCount(): Observable<number> {
    return this.repository.getCartItemsCount();
  }

  public queueCartItem(actionType: CartQueueActionType, data: CartQueueActionData): void {
    this.repository.queueCartItem(actionType, data);
  }

  public queueConfigurableBundle(data: ConfigurableBundleAddToCartModel): void {
    this.queueCartItem('addConfigurableBundle', {
      sku: data.id,
      data,
    } satisfies AddConfigurableBundleToCartActionData);
  }

  public deleteAllItemsFromCart(): Observable<CartModel> {
    return defer(() => {
      this.spinnerService.showLoader();

      return this.repository.deleteAllItemsFromCart().pipe(
        tap(() => {
          this.flashMessageService.showMessage('successDeleteAllItemsCart');
        }),
        finalize(() => void this.spinnerService.hideLoader()),
        catchError((error: unknown) => {
          this.shopErrorParser.parseAndShowRawErrorMessages(error, 'errorDeleteAllItemsCart');

          return throwError(() => error);
        }),
      );
    });
  }

  public addItemsCollection(collection: CartItemPayloadModel[], preprocessedErrors: ShopErrorItemModel[] = []): Observable<ShopCollectionResponseModel<CartModel>> {
    return defer(() => {
      this.spinnerService.showLoader();

      return this.repository.addItemsCollection(collection).pipe(
        map(result => {
          const errors: ShopErrorItemModel[] = result.errors.concat(preprocessedErrors);

          return {
            value: result.value,
            errors: errors,
          };
        }),
        tap((result) => {
          this.showItemsCollectionMessage(result.errors, 'successAddToCart');
        }),
        finalize(() => void this.spinnerService.hideLoader()),
        catchError((error: unknown) => {
          this.shopErrorParser.parseAndShowRawErrorMessages(error, 'errorAddToCart');

          return throwError(() => error);
        }),
      );
    });
  }

  public deleteItemsCollection(skus: string[], preprocessedErrors: ShopErrorItemModel[] = []): Observable<ShopCollectionResponseModel<CartModel>> {
    return defer(() => {
      this.spinnerService.showLoader();

      const payload: CartItemDeletePayloadModel[] = skus.map(sku => ({
        type: 'cart-item-delete',
        id: sku,
      }));

      return this.repository.deleteItemsCollection(payload).pipe(
        map(result => {
          const errors: ShopErrorItemModel[] = result.errors.concat(preprocessedErrors);

          return {
            value: result.value,
            errors: errors,
          };
        }),
        tap((result) => {
          this.showItemsCollectionMessage(result.errors, 'successRemoveFromCart');
        }),
        finalize(() => void this.spinnerService.hideLoader()),
        catchError((error: unknown) => {
          this.shopErrorParser.parseAndShowRawErrorMessages(error, 'errorRemoveFromCart');

          return throwError(() => error);
        }),
      );
    });
  }

  public getCartQueue(): Observable<CartQueueItem[]> {
    return this.repository.getCartQueue();
  }

  public productInCartCount(sku: string): Observable<number> {
    return this.getCart().pipe(
      map(cart => cart?.items.find(item => item.sku === sku)?.quantity ?? 0),
    );
  }

  public resetCartId(): void {
    this.repository.resetCartId();
  }

  public requestCart(): Observable<CartModel | undefined> {
    return this.repository.requestCart();
  }

  public initCartId(): Observable<string> {
    return this.repository.initCartId();
  }

  public addVoucherToCart(voucher: string): Observable<CartModel> {
    return this.repository.addVoucherToCart(voucher);
  }

  public removeVoucherFromCart(voucherCode: string): Observable<CartModel | undefined> {
    return this.repository.removeVoucherFromCart(voucherCode)
      .pipe(switchMap(() => this.requestCart().pipe(tap((cart) => {
        if (cart) {
          this.setCart(cart);
        } else {
          this.errorHandler.handleError(new Error('cart is undefined while refreshing in removeVoucherFromCart'));
        }
      }))));
  }

  public getVoucherListItems(): Observable<VoucherListItem[]> {
    return this.repository.getCart().pipe(map(cart => this.createVoucherListItems(cart)));
  }

  public getCartItemsCountView(): Observable<string> {
    return this.getCartItemsCount().pipe(
      map(count => this.getCountView(count)),
    );
  }

  public getProductInCartCountView(sku: string): Observable<string> {
    return this.productInCartCount(sku).pipe(
      map(count => this.getCountView(count)),
    );
  }

  private showItemsCollectionMessage(failedItems: ShopErrorItemModel[], message: FlashMessageType): void {
    if (!failedItems.length) {
      this.flashMessageService.showMessage(message);

      return;
    }

    const failedSkus: string = failedItems.map(item => item.itemPointer ?? item.message).join(', ');

    this.flashMessageService.showCustomMessage({
      message: `Folgende Artikel konnten nicht verarbeitet werden: ${failedSkus}`,
      theme: 'warning',
      duration: 10000,
    });
  }

  private getCountView(count: number): string {
    if (count === 0) {
      return '';
    }

    if (count > 999) {
      return '999+';
    }

    return count.toString();
  }

  private createVoucherListItems(cart: CartModel | undefined): VoucherListItem[] {
    const voucherListItems: VoucherListItem[] = [];

    if (!cart?.vouchers) {
      return [];
    }

    for (const voucher of cart.vouchers) {
      const voucherListItem: VoucherListItem = {
        name: voucher.displayName,
        code: voucher.code,
        productQuantity: voucher.discountPromotionQuantity,
        productName: '',
        imageUrl: '',
      };

      const cartItem: CartItemModel | undefined = cart.items.find(
        item => voucher.freeItemIds.includes(item.id),
      );

      if (cartItem) {
        voucherListItem.productName = cartItem.productData.details.name;
        voucherListItem.imageUrl = cartItem.productData.images[0]?.small;
        voucherListItems.push(voucherListItem);
      }
    }

    return voucherListItems;
  }
}
